/**
 * Voice mode: WS to the server voice gateway (server-side STT/TTS).
 *
 * Wire protocol (see deutero_interviews/api/voice.py): binary frames are
 * 0x02 + u32le sample_rate + PCM16LE mono in both directions, except that
 * with a page clock (`sampleClock`, a Recorder's capture clock) the mic goes
 * up as 0x04 + u64le page_frame + u32le sample_rate + PCM16LE mono —
 * page_frame being the clock's frame of the block's first sample — after a
 * `{"type":"rec_clock","sample_rate"}`. JSON control frames carry
 * turn-taking, barge-in, transcripts, replies and, for masking studies,
 * `rec_clear` (page frames to release from the recording's hold-back). Two turn-taking
 * modes, chosen by the server's `config` frame:
 *  - "vad" (hands-free): the mic streams while it is on, and the server ends
 *    each turn on semantic VAD and decides barge-in itself;
 *  - "hold" (push-to-talk): the browser marks each utterance's start and end.
 * Mic capture: getUserMedia → 16 kHz AudioContext → AudioWorklet (the mic
 * worklet, src/worklets.ts) → PCM16 frames up. Playback: scheduled
 * AudioBufferSources on a cursor — into the page's `playback.output` when it
 * gives one (the recorder's TTS tap, before any volume/mute, in the
 * recorder's context), else into a context of the socket's own.
 */
import type { InterviewerReply, StreamEvent } from "./types";
import type { PlayoutLog } from "./playout";
import { loadWorklet, MIC_WORKLET_SOURCE, workletUrl } from "./worklets";

const TAG_AUDIO = 0x02;
const TAG_MIC_FRAMED = 0x04;
const MIC_RATE = 16_000;

export function packAudio(pcm: Int16Array, sampleRate: number): ArrayBuffer {
  const out = new ArrayBuffer(5 + pcm.length * 2);
  const view = new DataView(out);
  view.setUint8(0, TAG_AUDIO);
  view.setUint32(1, sampleRate, true);
  // Payload starts at the odd offset 5, so copy via a byte view.
  const bytes = new Uint8Array(out, 5);
  bytes.set(new Uint8Array(pcm.buffer, pcm.byteOffset, pcm.length * 2));
  return out;
}

/** A 0x04 mic frame: the PCM stamped with its first sample's page frame. */
export function packMic(pcm: Int16Array, sampleRate: number, pageFrame: number): ArrayBuffer {
  const out = new ArrayBuffer(13 + pcm.length * 2);
  const view = new DataView(out);
  view.setUint8(0, TAG_MIC_FRAMED);
  view.setBigUint64(1, BigInt(pageFrame), true);
  view.setUint32(9, sampleRate, true);
  new Uint8Array(out, 13).set(new Uint8Array(pcm.buffer, pcm.byteOffset, pcm.length * 2));
  return out;
}

const isFrame = (x: unknown): x is number => Number.isSafeInteger(x) && (x as number) >= 0;

export function unpackAudio(
  frame: ArrayBuffer
): { pcm: Float32Array<ArrayBuffer>; rate: number } {
  const view = new DataView(frame);
  const rate = view.getUint32(1, true);
  const samples = (frame.byteLength - 5) >> 1;
  const pcm = new Float32Array(samples) as Float32Array<ArrayBuffer>;
  for (let i = 0; i < samples; i++) {
    pcm[i] = view.getInt16(5 + i * 2, true) / 32768;
  }
  return { pcm, rate };
}

export function floatToPcm16(samples: Float32Array): Int16Array {
  const out = new Int16Array(samples.length);
  for (let i = 0; i < samples.length; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]!));
    out[i] = Math.round(s * 32767);
  }
  return out;
}

/** What the study records, from the socket's `config` frame (effective
 *  recording: the survey policy and the participant's consent — see
 *  deutero_interviews/recording_policy.py). The recording itself is the
 *  page's Recorder (src/recorder.ts), whose state the widget shows; the
 *  socket never reports it broken, so `ok` is always true here. */
export interface SessionRecording {
  audio: boolean;
  video: boolean;
  ok: boolean;
}

export type TurnTaking = "vad" | "hold";

export interface VoiceCallbacks {
  onStreamEvent(event: StreamEvent): void;
  onTranscript(raw: string, answer: string): void;
  onReply(reply: InterviewerReply): void;
  onSpeaking(): void;
  onIdle(): void;
  /** Push-to-talk state — whether the mic is capturing this instant. Not to be
   *  confused with `onSessionRecording`, which is whether that audio is being
   *  kept. */
  onRecordingChange(recording: boolean): void;
  onSessionRecording(state: SessionRecording): void;
  onError(detail: string, fatal: boolean): void;
  /** Hands-free only: the participant's words so far this turn. */
  onPartial?(text: string): void;
  /** Hands-free only: the server heard the participant start / finish. */
  onTurn?(state: "user_speaking" | "user_done"): void;
  /** The turn-taking mode in effect (from `config`, or after a switch or a
   *  fallback to push-to-talk). */
  onTurnTaking?(mode: TurnTaking): void;
  /** The page clock's frame now on the capture side (a Recorder's
   *  `captureFrame()`), or null while there is none. Given and non-null, mic
   *  blocks go up as 0x04 frames stamped on it — live session or not. */
  sampleClock?(): number | null;
  /** That clock's rate (the Recorder's AudioContext sampleRate). */
  clockRate?(): number | null;
  /** Masking: the gateway releases page frames [start, end) of held-back
   *  participant audio, bleeping each [s, e] window (page frames) — for
   *  `Recorder.release`. */
  onRecClear?(start: number, end: number, bleep: [number, number][]): void;
  /** An interviewer audio chunk was scheduled (a Recorder activity signal). */
  onAudio?(): void;
  /** Recording: a turn's word timings, `{"type":"stt_words", message_id,
   *  source, span, words}` in page frames — for `Recorder.toSessionEvent`. */
  onTiming?(msg: unknown): void;
  /** The participant's mic stream while it is open, then null — so the page
   *  can feed its Recorder's mic input only while the mic is on. */
  onMicStream?(stream: MediaStream | null): void;
}

export interface VoiceOptions {
  /** Play the interviewer into `output` in `ctx` (the Recorder's TTS tap and
   *  context) instead of a context of the socket's own. The socket never
   *  closes or suspends a context it was given. */
  playback?: { ctx: AudioContext; output: AudioNode };
  /** The API origin the widget's worklet files are served from
   *  (`/widget/v1/`); null tries only the inline copy. */
  workletBase?: string | null;
  /** The page's PlayoutLog (src/playout.ts), or null while there is none:
   *  the gateway marks each item's audio with its message and its place in
   *  that message's audio (`tts_audio`) and sends its word times
   *  (`tts_words`); the log hears both, where each chunk played, the cut on a
   *  barge-in, and `idle`. */
  playout?: () => PlayoutLog<any> | null;
}

export class VoiceSession {
  private ws: WebSocket | null = null;
  private playCtx: AudioContext | null = null;
  private playCursor = 0;
  private playing: AudioBufferSourceNode[] = [];
  private micCtx: AudioContext | null = null;
  private micStream: MediaStream | null = null;
  private micNode: AudioWorkletNode | null = null;
  private recording = false;
  private opening: Promise<void> | null = null;
  private turnTaking: TurnTaking = "hold";
  private session: SessionRecording = { audio: false, video: false, ok: true };
  private closed = false;
  private sentClock: number | null = null;   // the rate last sent in rec_clock
  /** The message the next interviewer chunk belongs to, and where in its audio. */
  private speech: { id: string; audioS: number } | null = null;

  constructor(
    private url: string,
    private cb: VoiceCallbacks,
    private opts: VoiceOptions = {}
  ) {}

  async start(): Promise<void> {
    const ws = new WebSocket(this.url);
    ws.binaryType = "arraybuffer";
    this.ws = ws;
    ws.onmessage = (ev) => this.onMessage(ev);
    ws.onclose = (ev) => {
      if (!this.closed && ev.code === 4401) {
        this.cb.onError("voice session unauthorized", true);
      }
    };
    await new Promise<void>((resolve, reject) => {
      ws.onopen = () => {
        this.announceClock();
        resolve();
      };
      ws.onerror = () => reject(new Error("voice socket failed to connect"));
    });
  }

  private onMessage(ev: MessageEvent): void {
    if (ev.data instanceof ArrayBuffer) {
      const view = new DataView(ev.data);
      if (view.getUint8(0) === TAG_AUDIO) this.playChunk(ev.data);
      return;
    }
    let frame: any;
    try {
      frame = JSON.parse(ev.data);
    } catch {
      return;
    }
    if (frame.topic) {
      this.cb.onStreamEvent(frame as StreamEvent);
      return;
    }
    switch (frame.type) {
      case "config":
        this.setMode(frame.turn_taking === "vad" ? "vad" : "hold");
        this.session = {
          audio: !!frame.recording?.audio,
          video: !!frame.recording?.video,
          ok: true,
        };
        this.cb.onSessionRecording(this.session);
        break;
      case "rec_clear": {
        const bleep: unknown = frame.bleep ?? [];
        if (!isFrame(frame.start) || !isFrame(frame.end) || !Array.isArray(bleep)) break;
        if (!bleep.every((w) => Array.isArray(w) && w.length === 2 && isFrame(w[0]) && isFrame(w[1]))) break;
        this.cb.onRecClear?.(frame.start, frame.end, bleep as [number, number][]);
        break;
      }
      case "speaking":
        this.cb.onSpeaking();
        break;
      case "speech_cut":
        // The server overturned what was playing (a regenerated turn, or the
        // participant talking over it): drop the audio already scheduled.
        this.stopPlayback();
        break;
      case "partial":
        this.cb.onPartial?.(String(frame.text ?? ""));
        break;
      case "turn":
        this.cb.onTurn?.(frame.state === "user_done" ? "user_done" : "user_speaking");
        break;
      case "turn_taking":
        this.setMode(frame.mode === "vad" ? "vad" : "hold");
        break;
      case "idle":
        this.log()?.idle();
        this.cb.onIdle();
        break;
      case "tts_audio":
        this.markSpeech(frame);
        break;
      case "tts_words":
        if (typeof frame.message_id === "string" && frame.message_id && Array.isArray(frame.words)) {
          this.log()?.words(frame.message_id, frame.words);
        }
        break;
      case "stt_words":
        this.cb.onTiming?.(frame);
        break;
      case "transcript":
        this.cb.onTranscript(frame.text ?? "", frame.answer ?? "");
        break;
      case "reply":
        this.cb.onReply(frame.data as InterviewerReply);
        break;
      case "error":
        this.cb.onError(String(frame.detail ?? "voice error"), !!frame.fatal);
        break;
    }
  }

  // ── Playback ────────────────────────────────────────────────────────────

  private log(): PlayoutLog<any> | null {
    return this.opts.playout?.() ?? null;
  }

  /** `tts_audio`: the chunks that follow are message `message_id`'s audio
   *  from `offset_ms` on (offset 0: a new delivery). No id: a repair line,
   *  which belongs to no message. */
  private markSpeech(f: any): void {
    const log = this.log();
    const id = typeof f.message_id === "string" ? f.message_id : "";
    if (!id) {
      log?.idle();
      this.speech = null;
      return;
    }
    const offset = Number.isFinite(f.offset_ms) ? (f.offset_ms as number) : 0;
    if (!this.speech || this.speech.id !== id || offset === 0) log?.begin(id);
    this.speech = { id, audioS: offset / 1000 };
  }

  private playChunk(frame: ArrayBuffer): void {
    const { pcm, rate } = unpackAudio(frame);
    if (!pcm.length) return;
    const given = this.opts.playback;
    if (!this.playCtx) {
      this.playCtx = given?.ctx ?? new AudioContext();
      this.playCursor = 0;
    }
    const ctx = this.playCtx;
    void ctx.resume();
    const buffer = ctx.createBuffer(1, pcm.length, rate);
    buffer.copyToChannel(pcm, 0);
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(given?.output ?? ctx.destination);
    const at = Math.max(ctx.currentTime, this.playCursor);
    source.start(at);
    this.playCursor = at + buffer.duration;
    this.playing.push(source);
    const speech = this.speech;
    if (speech) {
      this.log()?.scheduled(speech.id, at, buffer.duration, speech.audioS);
      speech.audioS += buffer.duration;
    }
    source.onended = () => {
      this.playing = this.playing.filter((s) => s !== source);
      if (speech) this.log()?.ended(speech.id);
    };
    this.cb.onAudio?.();
  }

  /** Interviewer audio is scheduled or playing. */
  get isPlaying(): boolean {
    return this.playing.length > 0;
  }

  private stopPlayback(): void {
    // Whatever was playing is cut here, for the recording's word timings.
    if (this.playCtx) this.log()?.cut(null, this.playCtx.currentTime);
    this.speech = null;
    for (const source of this.playing) {
      try {
        source.stop();
      } catch {
        /* already ended */
      }
    }
    this.playing = [];
    this.playCursor = 0;
  }

  // ── Push-to-talk capture ────────────────────────────────────────────────

  get isRecording(): boolean {
    return this.recording;
  }

  get mode(): TurnTaking {
    return this.turnTaking;
  }

  /** Ask the server to switch turn-taking (it answers with `turn_taking`;
   *  "vad" is refused when its STT engine cannot stream). */
  setTurnTaking(mode: TurnTaking): void {
    if (this.recording) this.stopRecording();
    this.ws?.send(JSON.stringify({ type: "set_turn_taking", mode }));
  }

  private setMode(mode: TurnTaking): void {
    const changed = mode !== this.turnTaking;
    // Falling back to push-to-talk mid-stream: stop the open mic, so the
    // participant taps to answer instead of talking into nothing.
    if (changed && mode === "hold" && this.recording) this.stopRecording();
    this.turnTaking = mode;
    this.cb.onTurnTaking?.(mode);
  }

  /** Concurrent calls (the hands-free auto-open racing a tap) share one open. */
  startRecording(): Promise<void> {
    if (!this.opening) {
      this.opening = this.openMic().finally(() => {
        this.opening = null;
      });
    }
    return this.opening;
  }

  private async openMic(): Promise<void> {
    if (this.recording || !this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    // Push-to-talk: tapping the mic while TTS plays is barge-in. Hands-free:
    // turning the mic on is not — the server cuts in when words are heard.
    if (this.turnTaking === "hold") this.interrupt();
    this.micStream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true },
    });
    if (this.closed) {
      this.micStream.getTracks().forEach((t) => t.stop());
      this.micStream = null;
      return;
    }
    // A 16 kHz context makes the browser resample the mic for us.
    this.micCtx = new AudioContext({ sampleRate: MIC_RATE });
    // Opened outside a click (hands-free opens on the server's `config`), the
    // context can start suspended, and a suspended worklet never runs — the
    // server would hear nothing. Resume it explicitly.
    await this.micCtx.resume();
    await loadWorklet(
      this.micCtx,
      workletUrl(this.opts.workletBase, "mic-worklet.js"),
      MIC_WORKLET_SOURCE
    );
    const source = this.micCtx.createMediaStreamSource(this.micStream);
    this.micNode = new AudioWorkletNode(this.micCtx, "deutero-mic");
    this.micNode.port.onmessage = (ev: MessageEvent<Float32Array>) => {
      if (!this.recording || !this.micCtx) return;
      this.sendMic(ev.data, this.micCtx.sampleRate);
    };
    source.connect(this.micNode);
    // Worklets need a destination path to run; keep it silent.
    const sink = this.micCtx.createGain();
    sink.gain.value = 0;
    this.micNode.connect(sink).connect(this.micCtx.destination);

    this.recording = true;
    this.cb.onMicStream?.(this.micStream);
    if (this.turnTaking === "hold") {
      this.ws.send(JSON.stringify({ type: "start_utterance" }));
    }
    this.cb.onRecordingChange(true);
  }

  private clockFrame(): number | null {
    const f = this.cb.sampleClock?.() ?? null;
    return isFrame(f) ? f : null;
  }

  /** Tell the gateway the page clock's rate, once per rate, while there is a
   *  clock; the rate, or null. */
  private announceClock(): number | null {
    if (this.clockFrame() === null) return null;
    const rate = this.cb.clockRate?.() ?? this.playCtx?.sampleRate ?? null;
    if (!Number.isSafeInteger(rate) || (rate as number) <= 0) return null;
    if (this.sentClock !== rate && this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type: "rec_clock", sample_rate: rate }));
      this.sentClock = rate;
    }
    return this.sentClock === rate ? rate : null;
  }

  /** One mic block, just delivered by the worklet: its last sample is the
   *  newest, so its first is the block's length earlier on the page clock. */
  sendMic(samples: Float32Array, rate: number): void {
    if (this.ws?.readyState !== WebSocket.OPEN) return;
    const pcm = floatToPcm16(samples);
    const now = this.clockFrame();
    const clock = now === null ? null : this.announceClock();
    if (now === null || clock === null) {
      this.ws.send(packAudio(pcm, rate));
      return;
    }
    const first = Math.max(0, now - Math.round((samples.length / rate) * clock));
    this.ws.send(packMic(pcm, rate, first));
  }

  stopRecording(): void {
    if (!this.recording) return;
    this.recording = false;
    this.ws?.send(
      JSON.stringify({ type: this.turnTaking === "hold" ? "end_utterance" : "stop_listening" })
    );
    this.teardownMic();
    this.cb.onRecordingChange(false);
  }

  private teardownMic(): void {
    if (this.micStream) this.cb.onMicStream?.(null);
    this.micNode?.disconnect();
    this.micNode = null;
    this.micStream?.getTracks().forEach((t) => t.stop());
    this.micStream = null;
    void this.micCtx?.close();
    this.micCtx = null;
  }

  interrupt(): void {
    this.stopPlayback();
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type: "interrupt" }));
    }
  }

  /** Typed fallback on the voice socket; `masked` as in Api.sendMessage. */
  sendText(text: string, messageId = "", masked = false): void {
    const frame: Record<string, unknown> = { type: "text", text, message_id: messageId };
    if (masked) frame.masked = true;
    this.ws?.send(JSON.stringify(frame));
  }

  stop(): void {
    this.closed = true;
    this.recording = false;
    this.teardownMic();
    this.stopPlayback();
    // A context the page lent us (the Recorder's) is the page's to close.
    if (this.playCtx && this.playCtx !== this.opts.playback?.ctx) void this.playCtx.close();
    this.playCtx = null;
    this.ws?.close();
    this.ws = null;
  }
}
